import React from 'react';
import Content from '../components/content';
import Footer from '../components/footer';
import Layout from '../components/layout';
import Image from '../components/image';
import Video from '../components/video';
import CaseHeader from '../components/case-header';

const Playfulbet = () => (
    <Layout>
        <CaseHeader
            title="Playfulbet"
            role="Product Design & Frontend"
            time="2015&ndash;2017"
            imgSrc={require('../assets/images/playfulbet/header.gif')}
            alt="Soccer, tennis and football animated balls"
            description="Playfulbet is a social gaming platform where users can play against traditional sports events, such as FIFA or NBA events."
            quote="Our platform should be fun and create a unique experience with a strong focus on social interaction."
            quoteAuthor="Paco Maldonado"
            quoteAuthorRole="COO at Playfulbet"
        />
        <section>
            <Content kind="p" tag="h2">
                Intro
            </Content>
            <Content>
                Playfulbet{' '}
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://web.archive.org/web/20131028165349/http://playfulbet.com/competiciones/liga-bbva-16"
                >
                    launched in 2015
                </a>{' '}
                as an MVP and reached more than 10,000 user subscriptions within the first six weeks. The
                main goal was to redesign and strengthen the platform’s foundations, improve
                usability across all devices, and make it scalable for future features.
            </Content>
        </section>
        <section>
            <Content kind="p" tag="h2">
                Foundations
            </Content>
            <Content>
                My first task was to define a new navigation structure (with future features in mind)
                and a clear flow from signup to activation. To increase retention, we needed to
                guide new users to complete basic actions—such as playing a game or
                updating their profile—early in their journey, right after signup.
            </Content>
            <Content>
                Within the first two months, I designed a new UI kit, defined the navigation, built
                a responsive layout, and introduced a single-page app experience.
            </Content>
            <Image
                alt="The new UI Guide: typography scale, color palette and some basic components."
                src={require('../assets/images/playfulbet/ui-kit.jpg')}
            />
            <Image
                alt="The user's home page uses the new kit as a reference."
                src={require('../assets/images/playfulbet/dashboard.jpg')}
            />
            <Content>
                The platform went viral a few weeks later through marketing campaigns, growing from 10K
                to more than 150K new signups in less than a month. Activation improved
                with the new layout and navigation, plus new features such as event search, featured
                events, and faster load times. Improving retention and monetization became our next
                major focus.
            </Content>
        </section>
        <section>
            <Content kind="p" tag="h2">
                Chat
            </Content>
            <Content>
                Early user feedback was critical to understanding what they wanted,
                what was missing, what wasn’t useful, and what we could improve. We built an in-product
                chat for each event and made a point of talking to our users every day. Those
                conversations gave us clear direction on what to build, improve, or
                prioritize.
            </Content>
            <Content>
                We later used tools such as{' '}
                <a target="_blank" rel="noopener noreferrer" href="https://www.hotjar.com/">
                    Hotjar
                </a>{' '}
                and{' '}
                <a target="_blank" rel="noopener noreferrer" href="https://www.typeform.com/">
                    Typeform
                </a>{' '}
                to automate how we gathered and organized feedback.
            </Content>
        </section>
        <section>
            <Content kind="p" tag="h2">
                Custom Dashboard
            </Content>
            <Content>
                The dashboard let users see recommended events (based on recently played events,
                their own and their friends’ preferences, and more) and view their events in
                play, their activity, and pending results.
            </Content>
            <Image
                alt="Home page with dashboard user metrics."
                src={require('../assets/images/playfulbet/custom-dashboard.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Gamification
            </Content>
            <Content>
                We introduced a core gaming experience built around levels to improve
                retention. Levels were based on the number of events played and achievements,
                such as events played in specific sports, leagues, or special events.
            </Content>
            <Image
                alt="Public metrics on each user profile section."
                src={require('../assets/images/playfulbet/gamification.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Advertising
            </Content>
            <Content>
                Once retention improved, we moved into advertising as a monetization
                channel. We were careful about what we showed and where, because we wanted to keep the
                experience focused on the platform. The best results came from letting users earn
                points by watching short promotional videos or playing sponsored mini-games.
            </Content>
            <Image
                alt="We selected ads to offer a playful sport-related experience."
                src={require('../assets/images/playfulbet/ads.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Mobile app
            </Content>
            <Content>
                We designed and developed a mobile app to make the platform more engaging,
                since most of our users accessed it from mobile devices.
            </Content>
            <Image
                alt="First release of the Android mobile app with basic features."
                src={require('../assets/images/playfulbet/playfulbet-mobile.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Favorite teams and competitions
            </Content>
            <Content>
                For new users, a typical flow after signup was to find a match in a
                particular competition. Users in Barcelona were more likely to play with friends
                in F.C. Barcelona or R.C.D. Espanyol events than in the Japanese J1 football
                league.
            </Content>
            <Content>
                We built a system that let users mark competitions and teams as favorites,
                giving them more control and a more personalized experience. We could then recommend
                upcoming events and competitions to users with similar interests.
            </Content>
            <Content>
                With this feature, we improved retention: users could maintain a list of their
                preferred teams, check status and get activated more easily, and found more value
                in playing with familiar competitions and teams.
            </Content>
            <Image
                alt="Home page recommendation modules."
                src={require('../assets/images/playfulbet/teams-1.jpg')}
            />
            <Image
                alt="Marking a team or a league as a favorite."
                src={require('../assets/images/playfulbet/teams-2.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Interaction
            </Content>
            <Content>
                Our goal was to build a platform where users would play with friends. The look
                and feel had to be playful and friendly. That’s why CTAs and buttons were
                prominent and colorful, and the interactions with them were too. We drew
                inspiration from real arcade machines and video game interfaces.
            </Content>
            <Video
                alt="Part of the interactions when playing a sports event."
                src={require('../assets/images/playfulbet/video-1.mp4')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Testing
            </Content>
            <Content>
                Building a solid, modular UI was crucial—it allowed us to build new features
                and test them early. Our prototypes were real code shipped to production for
                specific users (we even had a small pool of users who wanted to test beta features), and that gave us early signal on whether to move
                forward with an idea or not.
            </Content>
        </section>
        <Footer>
            <a href="./typeform">
                <img
                    loading="lazy"
                    src={require('../assets/images/typeform/header-3.gif')}
                    alt={'Typeform use case'}
                />
            </a>
            <a href="./new-relic">
                <img
                    loading="lazy"
                    src={require('../assets/images/new-relic/header-3.gif')}
                    alt={'New Relic use case'}
                />
            </a>
        </Footer>
    </Layout>
);

export default Playfulbet;
